import React, { Dispatch, SetStateAction } from "react"
import { Percentage } from "../CampaignInput.styled"
import Input from "../../Ui/Form/Input"
import CampaignFixedPrice from "./CampaignFixedPrice"
import {
  Description,
  PriceRuleContainer,
  TypeSelector,
  TypeSelectorContainer
} from "./PriceRule.styled"
import {
  InputPriceRule,
  InputPriceRuleType,
  InputStoreMarket,
  StoreGroup
} from "lib/types/generated/graphql-types"
import { ReactComponent as PercentageIcon } from "images/icons/badge-percent.svg"
import { ReactComponent as FixedPriceIcon } from "images/icons/badge-dollar.svg"
import { ReactComponent as SetPriceIcon } from "images/icons/hand-holding-dollar.svg"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"
import FallbackInput from "../../Ui/Form/FallbackInput"

type Props = {
  priceRule: InputPriceRule
  setPriceRule: Dispatch<SetStateAction<InputPriceRule>>
  addedStoreMarkets: InputStoreMarket[]
  storeGroups: StoreGroup[] | undefined
  isCampaignOngoing?: boolean
}
const PriceRule = ({
  priceRule,
  setPriceRule,
  addedStoreMarkets,
  storeGroups,
  isCampaignOngoing
}: Props) => {
  const updateType = (type: InputPriceRuleType) => {
    setPriceRule({ type: type })
  }

  const addMoney = (currency: string, amount: number) => {
    setPriceRule({
      ...priceRule,
      monies: [...(priceRule?.monies ?? []), { key: currency, value: amount }]
    })
  }

  return (
    <>
      <h2>Price rule</h2>
      <Description>
        A price rule determines how the sale prices of the products you&apos;ve selected will be
        calculated.
      </Description>
      <PriceRuleContainer>
        <TypeSelectorContainer>
          <WithAuth
            requiredGroups={PermissionGroups.campaigns.write}
            fallback={
              <TypeSelector $active={priceRule.type === InputPriceRuleType.RelativeDiscount}>
                <PercentageIcon />
                <b>Percentage</b>
                Discount in percentage
              </TypeSelector>
            }
          >
            <TypeSelector
              $active={priceRule.type === InputPriceRuleType.RelativeDiscount}
              onClick={() => updateType(InputPriceRuleType.RelativeDiscount)}
            >
              <PercentageIcon />
              <b>Percentage</b>
              Discount in percentage
            </TypeSelector>
          </WithAuth>
          <WithAuth
            requiredGroups={PermissionGroups.campaigns.write}
            fallback={
              <TypeSelector $active={priceRule.type === InputPriceRuleType.FixedDiscount}>
                <FixedPriceIcon />
                <b>Fixed amount</b>
                Define a fixed amount of discount to be subtracted from base price
              </TypeSelector>
            }
          >
            <TypeSelector
              $active={priceRule.type === InputPriceRuleType.FixedDiscount}
              onClick={() => updateType(InputPriceRuleType.FixedDiscount)}
            >
              <FixedPriceIcon />
              <b>Fixed amount</b>
              Define a fixed amount of discount to be subtracted from base price
            </TypeSelector>
          </WithAuth>
          <WithAuth
            requiredGroups={PermissionGroups.campaigns.write}
            fallback={
              <TypeSelector $active={priceRule.type === InputPriceRuleType.SetPrice}>
                <SetPriceIcon />
                <b>Set price</b>
                Set a fixed sale price for products
              </TypeSelector>
            }
          >
            <TypeSelector
              $active={priceRule.type === InputPriceRuleType.SetPrice}
              onClick={() => updateType(InputPriceRuleType.SetPrice)}
            >
              <SetPriceIcon />
              <b>Set price</b>
              Set a fixed sale price for products
            </TypeSelector>
          </WithAuth>
        </TypeSelectorContainer>
        <>
          {priceRule?.type === InputPriceRuleType.RelativeDiscount && (
            <>
              <Percentage>
                <WithAuth
                  requiredGroups={PermissionGroups.campaigns.write}
                  fallback={
                    <FallbackInput label={"Percentage"} value={priceRule?.percentage?.toString()} />
                  }
                >
                  <Input
                    name="percentage"
                    type="number"
                    placeholder="Percentage, 1 - 100"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setPriceRule({ ...priceRule, percentage: parseInt(event.target.value) })
                    }
                    disabled={isCampaignOngoing}
                    label="Percentage % *"
                    defaultValue={priceRule?.percentage ?? ""}
                  />
                </WithAuth>
                <WithAuth
                  requiredGroups={PermissionGroups.campaigns.write}
                  fallback={
                    <FallbackInput
                      label={"Percentage decimals"}
                      value={priceRule?.percentageDecimals?.toString()}
                    />
                  }
                >
                  <Input
                    name="percentageDecimals"
                    type="number"
                    disabled={isCampaignOngoing}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                      setPriceRule({
                        ...priceRule,
                        percentageDecimals: parseInt(event.target.value)
                      })
                    }
                    placeholder="Percentage decimals"
                    label="Percentage decimals *"
                    defaultValue={priceRule?.percentageDecimals ?? ""}
                  />
                </WithAuth>
              </Percentage>
              <div>
                <strong>Formatted: </strong>
                {priceRule?.percentageDecimals === 0 && priceRule?.percentage
                  ? priceRule?.percentage + "%"
                  : priceRule?.percentage && priceRule?.percentageDecimals
                    ? priceRule?.percentage / 10 ** priceRule?.percentageDecimals + "%"
                    : "0"}
              </div>
            </>
          )}

          {(priceRule?.type === InputPriceRuleType.FixedDiscount ||
            priceRule?.type === InputPriceRuleType.SetPrice) && (
            <CampaignFixedPrice
              isCampaignOngoing={isCampaignOngoing ?? false}
              fixedPrice={priceRule?.monies ?? []}
              addMoney={addMoney}
              addedStoreMarkets={addedStoreMarkets}
              storeGroups={storeGroups}
              priceRule={priceRule}
              setPriceRule={setPriceRule}
            />
          )}
        </>
      </PriceRuleContainer>
    </>
  )
}

export default PriceRule
